import { api } from "./api.ts";

import type {
  ClaimsTransmissionHistoryType,
  TinType,
  TagType,
  RREDataType,
  ValidationDetailType,
  PaginatedResponse,
  PolicyType,
} from "types/DataTypes";

type GetRREImportLogsArgs = {
  rreId?: string | number;
  id?: string | number;
};

type UploadClaimsImportArgs = {
  id: string | number;
  file: FormData;
};

type SendFileArgs = {
  id: string | number;
  data: unknown;
};

type GetRresArgs = {
  searchTerm?: string;
  filters?: Record<string, string>;
};

type GetRreByIdArgs = {
  rreId: string | number;
};

type PatchRreArgs = {
  rreId: string | number;
  data: Partial<RREDataType>;
};

type CreateRreArgs = {
  data: Partial<RREDataType>;
};

type GetRreValidationsArgs = {
  id: string;
};

type GetTagsByRreIdArgs = {
  rreId: string;
};

type GetPoliciesByRreIdArgs = {
  rreId: string;
};

type GetTinsByRreIdArgs = {
  rreId: string;
  limit?: number;
  offset?: number;
};

type GetCmsSubmissionsByRreArgs = {
  id: string;
};

type CreateRreTinArgs = {
  data: Partial<TinType>;
};

type DeleteRreTinArgs = {
  id: string | number;
};

type PatchRreTinArgs = {
  id: string | number;
  data: Partial<TinType>;
};

type GetRreTinByIdArgs = {
  id: string | number;
};

type PatchRreApprovalArgs = {
  rreId: string | number;
  data: unknown;
};

export const rreAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getRresImporLogsByRreId: build.query<unknown, GetRREImportLogsArgs>({
      query: ({ rreId }) => ({
        url: `api/v1/rres/${rreId}/claim_import/`,
        method: "GET",
      }),
      providesTags: ["RREImportLogs"],
    }),

    getImportLogs: build.query<unknown, GetRREImportLogsArgs>({
      query: ({ id, rreId }) => ({
        url: `api/v1/rres/${id || rreId}/claim_import/`,
        method: "GET",
      }),
    }),
    uploadClaimsImportForRRE: build.mutation<unknown, UploadClaimsImportArgs>({
      query: ({ id, file }) => ({
        url: `api/v1/rres/${id}/claim_import/`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["RREImportLogs"],
    }),
    sendTinFile: build.mutation<unknown, SendFileArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/rres/${id}/send_tin_file/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [],
    }),
    sendQueryFile: build.mutation<unknown, SendFileArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/rres/${id}/send_query_file/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [],
    }),
    sendClaimFile: build.mutation<unknown, SendFileArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/rres/${id}/send_claim_file/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [],
    }),
    getRres: build.query<PaginatedResponse<RREDataType>, GetRresArgs>({
      query: ({ searchTerm, filters = {} }) => ({
        url: `api/v1/rres`,
        method: "GET",
        params: {
          ...(searchTerm ? { filter: searchTerm, ...filters } : { ...filters }),
        },
      }),
      providesTags: ["RREs"],
    }),
    getRreById: build.query<RREDataType, GetRreByIdArgs>({
      query: ({ rreId }) => ({
        url: `api/v1/rres/${rreId}/`,
        method: "GET",
      }),
      providesTags: ["RRE"],
    }),
    patchRreById: build.mutation<RREDataType, PatchRreArgs>({
      query: ({ rreId, data }) => ({
        url: `api/v1/rres/${rreId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["RRE", "RREValidations"],
    }),
    createRre: build.mutation<RREDataType, CreateRreArgs>({
      query: ({ data }) => ({
        url: `api/v1/rres/`,
        method: "POST",
        body: data,
      }),
      // invalidatesTags: ["RREs"],
    }),
    getTagsByRreId: build.query<TagType[], GetTagsByRreIdArgs>({
      query: ({ rreId }) => ({
        url: `api/v1/rres/${rreId}/tags`,
        method: "GET",
      }),
    }),
    getTinsByRreId: build.query<PaginatedResponse<TinType>, GetTinsByRreIdArgs>(
      {
        query: ({ rreId }) => ({
          url: `api/v1/rretins/`,
          method: "GET",
          params: { rre_id: rreId },
        }),
        providesTags: ["RRETins"],
      },
    ),
    getCmsSubmissionsByRre: build.query<
      ClaimsTransmissionHistoryType[],
      GetCmsSubmissionsByRreArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/cms_transmission_logs/`,
        method: "GET",
        params: { rre_id: id },
      }),
    }),
    getPoliciesByRreId: build.query<
      PaginatedResponse<PolicyType>,
      GetPoliciesByRreIdArgs
    >({
      query: ({ rreId }) => ({
        url: `api/v1/policies/`,
        method: "GET",
        params: { rre_id: rreId },
      }),
      providesTags: ["RREPolicies"],
    }),
    getRreValidations: build.query<
      ValidationDetailType[],
      GetRreValidationsArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/rres/${id}/validations/`,
        method: "GET",
      }),
      providesTags: ["RREValidations"],
    }),
    getRreTinValidations: build.query<ValidationDetailType[], { id: string }>({
      query: ({ id }) => ({
        url: `api/v1/rretins/${id}/validations/`,
        method: "GET",
      }),
      providesTags: ["RRETinValidations"],
    }),
    createRreTin: build.mutation<TinType, CreateRreTinArgs>({
      query: ({ data }) => ({
        url: `api/v1/rretins/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["RRETins", "RREValidations"],
    }),
    deleteRreTin: build.mutation<void, DeleteRreTinArgs>({
      query: ({ id }) => ({
        url: `api/v1/rretins/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["RRETins", "RREValidations"],
    }),
    patchRreTin: build.mutation<TinType, PatchRreTinArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/rretins/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["RRETins", "RRETinValidations", "RREValidations"],
    }),
    getRreTinById: build.query<TinType, GetRreTinByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/rretins/${id}/`,
        method: "GET",
      }),
      providesTags: ["RRETins"],
    }),
    patchRreApprovalById: build.mutation<unknown, PatchRreApprovalArgs>({
      query: ({ rreId, data }) => ({
        url: `api/v1/rres/${rreId}/set_approval_for_cms_submission/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["RREValidations"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRreTinValidationsQuery,
  useCreateRreTinMutation,
  useDeleteRreTinMutation,
  usePatchRreTinMutation,
  useGetRreTinByIdQuery,
  useGetRreValidationsQuery,
  useGetTagsByRreIdQuery,
  useGetTinsByRreIdQuery,
  useGetPoliciesByRreIdQuery,
  useCreateRreMutation,
  useUploadClaimsImportForRREMutation,
  useGetCmsSubmissionsByRreQuery,
  useGetRresQuery,
  useGetRreByIdQuery,
  usePatchRreByIdMutation,
  useGetRresImporLogsByRreIdQuery,
  useGetImportLogsQuery,
  usePatchRreApprovalByIdMutation,
  useSendTinFileMutation,
  useSendClaimFileMutation,
  useSendQueryFileMutation,
} = rreAPI;
